import type { Moment } from "moment-timezone";

/**
 * NOTE: This is currently only used to calculate the Expiration Date for OTC contracts
 * Calculates the last trading day for short-dated options on new crop futures.
 *
 * The last day of trading is the last Friday which precedes by at least two business days
 * the last business day of the calendar month preceding the option's named expiry month.
 *
 * @param expiryMonth - The named expiry month of the option
 * @returns The last trading date for the option
 */
function baseCalculateLastTradingDay(expiryMoment: Moment): Moment {
  // Get the month preceding the expiry month
  const precedingMonth = expiryMoment.clone().subtract(1, 'month');

  // Find the last business day of the preceding month
  let lastBusinessDay = precedingMonth.clone().endOf('month');

  // Move backwards to find the last business day (Mon-Fri)
  while (lastBusinessDay.day() === 0 || lastBusinessDay.day() === 6) {
    lastBusinessDay.subtract(1, 'day');
  }

  // Go back 2 business days from the last business day
  let daysToSubtract = 2;
  let targetDate = lastBusinessDay.clone();

  while (daysToSubtract > 0) {
    targetDate.subtract(1, 'day');
    // Only count weekdays (Monday = 1, Friday = 5)
    if (targetDate.day() !== 0 && targetDate.day() !== 6) {
      daysToSubtract--;
    }
  }

  // Now find the last Friday that is on or before this target date
  let lastFriday = targetDate.clone();

  // If the target date is after Friday (Saturday = 6 or Sunday = 0),
  // go back to the previous Friday
  if (lastFriday.day() === 6) {
    lastFriday.subtract(1, 'day'); // Saturday to Friday
  } else if (lastFriday.day() === 0) {
    lastFriday.subtract(2, 'days'); // Sunday to Friday
  } else if (lastFriday.day() < 5) {
    // If it's Monday (1) through Thursday (4), go back to previous Friday
    const daysFromFriday = 5 - lastFriday.day();
    lastFriday.subtract(7 - daysFromFriday, 'days');
  }
  // If it's already Friday (5), we keep it as is

  return lastFriday;
}

type LastTradingDayOverride = {
  matches: (date: Moment) => boolean;
  apply: (date: Moment) => Moment;
};

const LAST_TRADING_DAY_OVERRIDES: ReadonlyArray<LastTradingDayOverride> = [
  {
    // Christmas -> Christmas Eve, every year. Moment months are zero-based.
    matches: (date: Moment): boolean => date.month() === 11 && date.date() === 25,
    apply: (date: Moment): Moment => date.date(24),
  },
];

/**
 * Applies the first matching override to the base expiration calculation.
 * Keep the calculated Moment's time and timezone until returning the Date.
 */
export function calculateLastTradingDay(expiryMoment: Moment): Date {
  const lastTradingDay = baseCalculateLastTradingDay(expiryMoment);
  const override = LAST_TRADING_DAY_OVERRIDES.find(
    (rule: LastTradingDayOverride): boolean => rule.matches(lastTradingDay),
  );

  return (override ? override.apply(lastTradingDay) : lastTradingDay).toDate();
}
